import clsx from 'clsx';
import type { ReactNode } from 'react';

export type AlertTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

// Same `bg-X-50 dark:bg-X-900` shape that fix-wave 2b's Field Tools (group C)
// and Insights (group F) sweeps converged on independently for status-fill
// banners — this is that shape made into one component instead of 45
// hand-copies of it.
//
// `success`/`warning`/`danger`/`info` deliberately do NOT reuse
// `ui/Badge.tsx`'s tone hexes: Badge's `success` is teal (the brand accent,
// used for workflow-status chips like "Finalized") and its `info` is sky —
// neither matches what the app's actual message banners have used since
// before Badge existed (green for success, blue for info; see
// BillRunDetail.tsx, ReadingPage.tsx, CommissionPage.tsx, BillVerification.tsx
// etc.). Reusing Badge's hexes here would have "fixed" the banners into a
// shade nothing on screen currently uses. `blue`-vs-`sky` unification is
// explicitly out of scope for this wave (see fix-wave-b-report.md) — `info`
// here just documents the value banners already render.
//
// `neutral` mirrors Badge's `neutral` exactly (`stone-100/700` light,
// `stone-700/300` dark) rather than following the `X-50/900` shape: a
// `stone-50` light fill is nearly indistinguishable from `--surface`
// (#ffffff), and a `stone-900` dark fill (#1c1917) is BYTE-IDENTICAL to
// `--surface` dark — an invisible-on-page trap even worse than the
// `--surface-raised` dark one `stone-800` (#292524) sets below. Every
// tone's own text/bg ratio (both themes) and every tone's dark bg against
// `surfaceRaised` dark are asserted in src/test/contrast.test.ts, not just
// hand-verified — see the ratios in that file's comments and in
// fix-wave-b-report.md.
const TONES: Record<AlertTone, string> = {
  neutral: 'bg-stone-100 border-stone-300 text-stone-700 dark:bg-stone-700 dark:border-stone-600 dark:text-stone-300',
  success: 'bg-green-50 border-green-200 text-green-900 dark:bg-green-900 dark:border-green-800 dark:text-green-100',
  warning: 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900 dark:border-amber-800 dark:text-amber-100',
  danger: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900 dark:border-red-800 dark:text-red-100',
  info: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900 dark:border-blue-800 dark:text-blue-100',
};

// Exported so a full-width bar (shell/MaintenanceBanner) reuses the same
// contrast-tested tone pairs instead of hand-copying the hexes again.
export const ALERT_TONE_CLASSES: Readonly<Record<AlertTone, string>> = TONES;

export function Alert({
  tone = 'neutral',
  title,
  className,
  children,
}: {
  tone?: AlertTone;
  title?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={clsx('rounded-xl border p-4 text-sm', TONES[tone], className)}>
      {title && <p className="font-semibold mb-1">{title}</p>}
      {children}
    </div>
  );
}
